import {
  SampleDocumentRef,
  SerializedClassifier,
} from "@/prisma-package/src/utils/document-type-builtins";
import { POSSIBLE_SCORE, QUALIFY_SCORE } from "./corpus-validation.utils";

/**
 * The user-facing vocabulary for detection results. Raw scores and the
 * qualify/possible thresholds stay an Advanced-surface concept; everything
 * a non-technical user sees goes through these status words.
 */

export type DetectionStatus = "recognized" | "close" | "notRecognized";

export const DETECTION_STATUS_LABELS: Record<DetectionStatus, string> = {
  recognized: "Recognized",
  close: "Close",
  notRecognized: "Not recognized",
};

export const detectionStatusForScore = (
  score: number,
  excluded = false
): DetectionStatus => {
  if (excluded) return "notRecognized";
  if (score >= QUALIFY_SCORE) return "recognized";
  if (score >= POSSIBLE_SCORE) return "close";
  return "notRecognized";
};

/**
 * Progress toward "recognized" as a 0-1 fraction, for per-document meters.
 * Excluded documents render as (almost) empty regardless of raw score.
 */
export const detectionProgress = (score: number, excluded = false): number => {
  if (excluded) return 0;
  return Math.max(0, Math.min(1, score / QUALIFY_SCORE));
};

/**
 * One-line explanation of why a document holds its status, shown under the
 * status pill. The exclusion phrase is rendered separately by the caller.
 */
export const describeDetectionStatus = (
  status: DetectionStatus,
  options?: { excluded?: boolean; matchCount?: number }
): string | null => {
  if (options?.excluded) {
    return "Ruled out — the document contains a disqualifying phrase.";
  }
  if (status === "close") {
    return "Almost recognized — one more matching phrase would get it there.";
  }
  if (status === "notRecognized") {
    return (options?.matchCount ?? 0) === 0
      ? "No phrases matched. It may use different wording than your other examples."
      : "A few phrases matched, but not enough to recognize it.";
  }
  return null;
};

/**
 * Persisted health of a saved document type, derived from the sample scores
 * recorded at save time — no PDF re-extraction needed on list surfaces.
 *
 * - working:       every scored sample is recognized
 * - partial:       some but not all samples are recognized
 * - notDetecting:  samples exist but none are recognized
 * - needsExamples: phrases exist but there are no samples to check against
 * - needsPhrases:  no detection phrases at all
 * - unchecked:     samples predate score recording — open the type to check
 */
export type TypeHealthState =
  | "working"
  | "partial"
  | "notDetecting"
  | "needsExamples"
  | "needsPhrases"
  | "unchecked";

export type TypeHealth = {
  state: TypeHealthState;
  recognized: number;
  total: number;
};

// User-facing labels are calls to action, not diagnoses: partial and
// notDetecting both read "Needs training" (color conveys degree), and the
// not-set-up states read as a neutral starting point rather than a problem.
export const TYPE_HEALTH_LABELS: Record<TypeHealthState, string> = {
  working: "Working well",
  partial: "Needs training",
  notDetecting: "Needs training",
  needsExamples: "Not trained yet",
  needsPhrases: "Not trained yet",
  unchecked: "Not checked yet",
};

/**
 * Whether the health was measured against scored examples, as opposed to the
 * not-set-up states. Battle-tested built-ins carry no examples, so their
 * unmeasured states shouldn't render as a training call-to-action — they get
 * a badge only once real scored samples exist.
 */
export const hasMeasuredHealth = (health: TypeHealth): boolean =>
  health.state === "working" ||
  health.state === "partial" ||
  health.state === "notDetecting";

export const computeTypeHealth = (
  classifier: SerializedClassifier | null | undefined,
  sampleDocuments: SampleDocumentRef[] | null | undefined
): TypeHealth => {
  const samples = sampleDocuments ?? [];
  if (!classifier || classifier.patterns.length === 0) {
    return { state: "needsPhrases", recognized: 0, total: samples.length };
  }
  if (samples.length === 0) {
    return { state: "needsExamples", recognized: 0, total: 0 };
  }
  const scored = samples.filter((sample) => sample.score !== undefined);
  if (scored.length === 0) {
    return { state: "unchecked", recognized: 0, total: samples.length };
  }
  const recognized = scored.filter(
    (sample) => (sample.score ?? 0) >= QUALIFY_SCORE
  ).length;
  const state =
    recognized === scored.length
      ? "working"
      : recognized === 0
        ? "notDetecting"
        : "partial";
  return { state, recognized, total: scored.length };
};

/**
 * Sub-line under a health badge: what's true, and what to do about it.
 */
export const describeTypeHealth = (health: TypeHealth): string => {
  switch (health.state) {
    case "needsPhrases":
    case "needsExamples":
      return "Train it with a few example documents.";
    case "unchecked":
      return "Open Train & Test to check detection.";
    case "working":
      return `${health.recognized} of ${health.total} example${
        health.total === 1 ? "" : "s"
      } recognized`;
    default:
      return `${health.recognized} of ${health.total} example${
        health.total === 1 ? "" : "s"
      } recognized — train with more documents`;
  }
};
